/**
 * EndpointHealthConfigPage.jsx
 *
 * Settings page for configuring UEM Endpoint Health Monitoring.
 * Three tabs:
 *   1. Health Rules      — threshold rules that trigger alerts & action items
 *   2. Remediation Wizards — auto-fix procedures linked to health rules
 *   3. Alert History     — read-only view of fired alerts with resolve action
 *
 * Backend API: /api/EndpointHealth
 *
 * @module SettingsPages/EndpointHealthConfigPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaShieldAlt, FaPlus, FaEdit, FaTrashAlt, FaSpinner, FaTimes,
    FaSave, FaExclamationTriangle, FaRobot, FaBell, FaBellSlash,
    FaToggleOn, FaToggleOff, FaHistory, FaCheckCircle, FaTimesCircle,
    FaMagic, FaListAlt, FaServer, FaCode, FaSearch
} from 'react-icons/fa';
import '@/styles/ModuleTemplateConfigStyles.scss';
import * as Sentry from '@sentry/react';
import RemediationStepEditor from './components/remediation/RemediationStepEditor';
import WizardPreviewPanel from './components/remediation/WizardPreviewPanel';
import ScriptLibraryTab from './components/remediation/ScriptLibraryTab';
import { REMEDIATION_CATEGORIES } from './components/remediation/remediationActionMeta';
import './components/remediation/RemediationConfig.scss';

// ── Constants ────────────────────────────────────────────────────────────────

const METRIC_LABELS = {
    StorageUsedPercent:        'Storage Used %',
    FreeStorageBytes:          'Free Storage (bytes)',
    BatteryChargePercent:      'Battery Charge %',
    BatteryChargeCycles:       'Battery Charge Cycles',
    ComplianceState:           'Compliance State',
    IsEncrypted:               'Disk Encrypted',
    DefenderEnabled:           'Defender Enabled',
    RealTimeProtectionEnabled: 'Real-Time Protection Enabled',
    FirewallEnabled:           'Firewall Enabled',
    DefenderUpToDate:          'Defender Definitions Up-to-Date',
    JailBroken:                'Jailbroken / Rooted',
    DeviceThreatLevel:         'Device Threat Level',
    ActiveThreatAlerts:        'Active Threat Alerts',
    CertExpiryDays:            'Days Until Cert Expires',
    DaysSinceLastSync:         'Days Since Last Sync',
    DaysSinceLastSignIn:       'Days Since Last Sign-In',
    DaysSinceEnrollment:       'Days Since Enrollment',
    ManagementState:           'Management State',
    OsOutdated:                'OS Version Below'
};

// ── Metric type classification ───────────────────────────────────────────────

/** Metrics that are purely true/false — operator is always Equals, value is a toggle. */
const BOOLEAN_METRICS = new Set([
    'IsEncrypted', 'DefenderEnabled', 'RealTimeProtectionEnabled',
    'FirewallEnabled', 'DefenderUpToDate', 'JailBroken'
]);

/** Metrics compared as dotted version strings (e.g. OS build numbers). */
const VERSION_METRICS = new Set([
    'OsOutdated'
]);

/** Metrics that have a fixed set of known string values. */
const ENUM_METRICS = {
    ComplianceState: [
        { value: 'compliant',       label: 'Compliant' },
        { value: 'noncompliant',    label: 'Non-Compliant' },
        { value: 'inGracePeriod',   label: 'In Grace Period' },
        { value: 'configManager',   label: 'Config Manager' },
        { value: 'unknown',         label: 'Unknown' },
        { value: 'notApplicable',   label: 'Not Applicable' }
    ],
    DeviceThreatLevel: [
        { value: 'unavailable', label: 'Unavailable' },
        { value: 'none',        label: 'None (Clean)' },
        { value: 'low',         label: 'Low' },
        { value: 'medium',      label: 'Medium' },
        { value: 'high',        label: 'High' },
        { value: 'notSet',      label: 'Not Set' }
    ],
    ManagementState: [
        { value: 'managed',           label: 'Managed' },
        { value: 'retirePending',     label: 'Retire Pending' },
        { value: 'retireFailed',      label: 'Retire Failed' },
        { value: 'wipePending',       label: 'Wipe Pending' },
        { value: 'wipeFailed',        label: 'Wipe Failed' },
        { value: 'unhealthy',         label: 'Unhealthy' },
        { value: 'deletePending',     label: 'Delete Pending' },
        { value: 'retireIssued',      label: 'Retire Issued' },
        { value: 'wipeIssued',        label: 'Wipe Issued' },
        { value: 'wipeCanceled',      label: 'Wipe Canceled' },
        { value: 'retireCanceled',    label: 'Retire Canceled' },
        { value: 'discovered',        label: 'Discovered' }
    ]
};

/** Metrics where lower = better (alert when value exceeds threshold). */
const NUMERIC_METRICS = new Set([
    'StorageUsedPercent', 'FreeStorageBytes', 'BatteryChargePercent',
    'BatteryChargeCycles', 'CertExpiryDays', 'DaysSinceLastSync', 'DaysSinceLastSignIn',
    'DaysSinceEnrollment', 'ActiveThreatAlerts'
]);

/** Returns 'boolean' | 'enum' | 'numeric' | 'version' for a given metricType key. */
const getMetricKind = (metricType) => {
    if (BOOLEAN_METRICS.has(metricType)) return 'boolean';
    if (VERSION_METRICS.has(metricType)) return 'version';
    if (ENUM_METRICS[metricType])        return 'enum';
    if (NUMERIC_METRICS.has(metricType)) return 'numeric';
    return 'numeric';
};

/**
 * Returns the best default operator for a metric type.
 * boolean → Equals, enum → Equals, version → LessThan, numeric → depends on metric.
 */
const defaultOperatorFor = (metricType) => {
    const kind = getMetricKind(metricType);
    if (kind === 'boolean') return 'Equals';
    if (kind === 'enum')    return 'Equals';
    if (kind === 'version') return 'LessThan';   // alert when the OS build is BELOW the baseline
    // For CertExpiryDays and Days-since metrics, alert when value is LOW (i.e. LessThanOrEqual)
    if (metricType === 'CertExpiryDays' || metricType === 'FreeStorageBytes') return 'LessThanOrEqual';
    return 'GreaterThanOrEqual';
};

/** Returns the best default threshold value for a metric type. */
const defaultThresholdFor = (metricType) => {
    const kind = getMetricKind(metricType);
    if (kind === 'boolean') return 'true';   // alert when the metric IS true (or false for inverted ones below)
    if (kind === 'version') return '10.0.19045';  // baseline OS build; alert when device is older
    if (kind === 'enum') {
        const opts = ENUM_METRICS[metricType];
        return opts?.[0]?.value ?? '';
    }
    // Sensible numeric defaults
    const defaults = {
        StorageUsedPercent:   '90',
        BatteryChargePercent: '20',
        BatteryChargeCycles:  '500',
        CertExpiryDays:       '30',
        DaysSinceLastSync:    '14',
        DaysSinceLastSignIn:  '30',
        DaysSinceEnrollment:  '7',
        ActiveThreatAlerts:   '1',
        FreeStorageBytes:     '5368709120'  // 5 GB
    };
    return defaults[metricType] ?? '';
};

/**
 * Unwraps the standard settings API envelope so select controls consistently
 * receive an array of records instead of the outer `{ success, data }` object.
 */
const readWrappedCollection = async (response, collectionName) => {
    const payload = await response.json().catch(() => null);

    if (payload?.success === false) {
        toast.error(payload.message ?? `Failed to load ${collectionName}`);
        return [];
    }

    return Array.isArray(payload?.data) ? payload.data : [];
};

/** Human-readable explanation of what the current condition means. */
const describeCondition = (metricType, operator, thresholdValue, secondaryThresholdValue) => {
    const kind = getMetricKind(metricType);
    const label = METRIC_LABELS[metricType] ?? metricType;

    if (kind === 'boolean') {
        const wantTrue = thresholdValue === 'true' || thresholdValue === 'True';
        if (['OsOutdated', 'JailBroken'].includes(metricType)) {
            return wantTrue
                ? `🔴 Alert when the device ${label} is detected`
                : `🟢 Alert when ${label} is NOT detected (unusual — double-check intent)`;
        }
        return wantTrue
            ? `🟢 Alert when ${label} is enabled / true`
            : `🔴 Alert when ${label} is disabled / false — this is the recommended setting for security checks`;
    }

    if (kind === 'enum') {
        const opts = ENUM_METRICS[metricType] ?? [];
        const valueLabel = opts.find(o => o.value === thresholdValue)?.label ?? thresholdValue;
        const opLabel = operator === 'NotEquals' ? 'is NOT' : 'is';
        return `🔔 Alert when ${label} ${opLabel} "${valueLabel}"`;
    }

    if (kind === 'version') {
        const opMap = { GreaterThan: 'newer than', GreaterThanOrEqual: 'at or newer than', LessThan: 'older than', LessThanOrEqual: 'at or older than', Equals: 'exactly', NotEquals: 'not' };
        const opPhrase = opMap[operator] ?? operator;
        return `🔔 Alert when the device OS version is ${opPhrase} ${thresholdValue}`;
    }

    // Numeric
    if (secondaryThresholdValue) {
        return `🔔 Alert when ${label} is between ${thresholdValue} and ${secondaryThresholdValue}`;
    }
    const opMap = { GreaterThan: '>', GreaterThanOrEqual: '≥', LessThan: '<', LessThanOrEqual: '≤', Equals: '=', NotEquals: '≠' };
    const opSym = opMap[operator] ?? operator;
    let units = '';
    if (metricType === 'StorageUsedPercent' || metricType === 'BatteryChargePercent') units = '%';
    if (metricType.startsWith('DaysSince') || metricType === 'CertExpiryDays') units = ' days';
    if (metricType === 'FreeStorageBytes') units = ' bytes';
    return `🔔 Alert when ${label} ${opSym} ${thresholdValue}${units}`;
};

const OPERATOR_LABELS = {
    GreaterThan:          '>',
    GreaterThanOrEqual:   '>=',
    LessThan:             '<',
    LessThanOrEqual:      '<=',
    Equals:               '=',
    NotEquals:            '≠',
    Between:              'between',
    NotBetween:           'not between'
};

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const RISK_LEVELS = ['Low', 'Medium', 'High'];

const PRIORITY_COLOURS = {
    Low:      '#6b7280',
    Medium:   '#f59e0b',
    High:     '#ef4444',
    Critical: '#7c3aed'
};

const emptyRule = () => ({
    name: '',
    description: '',
    metricType: 'StorageUsedPercent',
    operator: 'GreaterThanOrEqual',
    thresholdValue: '',
    secondaryThresholdValue: '',
    operatingSystemFilter: '',
    cooldownMinutes: 1440,
    consecutiveBreachesRequired: 1,
    autoResolveOnRecovery: true,
    alertPriority: 'Medium',
    templateKey: '',
    assignToUserId: '',
    assignToDepartmentId: '',
    overrideBoardId: '',
    actionItemTags: '',
    autoRemediationEnabled: false,
    remediationWizardId: '',
    autoExecuteRemediation: false,
    isActive: true,
    priority: 100,
    sortOrder: 0
});

const emptyWizard = () => ({
    name: '',
    description: '',
    category: 'Storage',
    icon: '',
    colour: '#f63040',
    supportsAutoExecution: false,
    maxAutoExecutionsPerDay: 3,
    estimatedDurationMinutes: 5,
    timeoutMinutes: 30,
    supportedOperatingSystems: 'Windows',
    requiresDeviceOnline: true,
    requiresElevation: false,
    riskLevel: 'Low',
    isActive: true,
    sortOrder: 0,
    actionItemTemplateKey: '',
    actionItemBoardId: '',
    steps: []
});

// ── Tab: Health Rules ────────────────────────────────────────────────────────

const HealthRulesTab = ({ apiFetch }) => {
    const [rules, setRules]       = useState([]);
    const [wizards, setWizards]   = useState([]);
    const [metricTypes, setMetricTypes] = useState([]);
    const [operators, setOperators]     = useState([]);
    const [boards, setBoards]     = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [showModal, setShowModal]     = useState(false);
    const [editingId, setEditingId]     = useState(null);
    const [form, setForm]               = useState(emptyRule());
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [rulesRes, metricRes, opRes, wizRes, boardsRes, templatesRes] = await Promise.all([
                apiFetch('/api/EndpointHealth/rules'),
                apiFetch('/api/EndpointHealth/rules/metric-types'),
                apiFetch('/api/EndpointHealth/rules/operators'),
                apiFetch('/api/EndpointHealth/wizards'),
                apiFetch('/api/KanbanBoards'),
                apiFetch('/api/Templates')
            ]);
            if (rulesRes.ok)  setRules(await rulesRes.json());
            if (metricRes.ok) setMetricTypes(await metricRes.json());
            if (opRes.ok)     setOperators(await opRes.json());
            if (wizRes.ok)    setWizards(await wizRes.json());
            if (boardsRes.ok) setBoards(await readWrappedCollection(boardsRes, 'boards'));
            if (templatesRes.ok) setTemplates(await readWrappedCollection(templatesRes, 'templates'));
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load health rules');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { fetchAll(); }, [fetchAll]);

    const openCreate = () => { setEditingId(null); setForm(emptyRule()); setShowModal(true); };
    const openEdit   = (r) => {
        setEditingId(r.endpointHealthRuleId);
        setForm({
            name:                        r.name ?? '',
            description:                 r.description ?? '',
            metricType:                  r.metricType ?? 'StorageUsedPercent',
            operator:                    r.operator ?? 'GreaterThanOrEqual',
            thresholdValue:              r.thresholdValue ?? '',
            secondaryThresholdValue:     r.secondaryThresholdValue ?? '',
            operatingSystemFilter:       r.operatingSystemFilter ?? '',
            cooldownMinutes:             r.cooldownMinutes ?? 1440,
            consecutiveBreachesRequired: r.consecutiveBreachesRequired ?? 1,
            autoResolveOnRecovery:       r.autoResolveOnRecovery ?? true,
            alertPriority:               r.alertPriority ?? 'Medium',
            templateKey:                 r.templateKey ?? '',
            assignToUserId:              r.assignToUserId ?? '',
            assignToDepartmentId:        r.assignToDepartmentId ?? '',
            overrideBoardId:             r.overrideBoardId ?? '',
            actionItemTags:              r.actionItemTags ?? '',
            autoRemediationEnabled:      r.autoRemediationEnabled ?? false,
            remediationWizardId:         r.remediationWizardId ?? '',
            autoExecuteRemediation:      r.autoExecuteRemediation ?? false,
            isActive:                    r.isActive ?? true,
            priority:                    r.priority ?? 100,
            sortOrder:                   r.sortOrder ?? 0
        });
        setShowModal(true);
    };
    const closeModal = () => { setShowModal(false); setEditingId(null); setForm(emptyRule()); };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'metricType') {
            setForm(prev => ({
                ...prev,
                metricType:              value,
                operator:                defaultOperatorFor(value),
                thresholdValue:          defaultThresholdFor(value),
                secondaryThresholdValue: ''
            }));
            return;
        }
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim())           { toast.error('Name is required'); return; }
        if (!form.thresholdValue.trim()) { toast.error('Threshold value is required'); return; }

        setSaving(true);
        try {
            const payload = {
                ...form,
                cooldownMinutes:             parseInt(form.cooldownMinutes, 10) || 1440,
                consecutiveBreachesRequired: parseInt(form.consecutiveBreachesRequired, 10) || 1,
                priority:                    parseInt(form.priority, 10) || 100,
                sortOrder:                   parseInt(form.sortOrder, 10) || 0,
                assignToUserId:              form.assignToUserId !== '' ? parseInt(form.assignToUserId, 10) : null,
                assignToDepartmentId:        form.assignToDepartmentId !== '' ? parseInt(form.assignToDepartmentId, 10) : null,
                overrideBoardId:             form.overrideBoardId !== '' ? parseInt(form.overrideBoardId, 10) : null,
                remediationWizardId:         form.remediationWizardId !== '' ? parseInt(form.remediationWizardId, 10) : null,
                secondaryThresholdValue:     form.secondaryThresholdValue || null,
                operatingSystemFilter:       form.operatingSystemFilter || null,
                templateKey:                 form.templateKey || null,
                actionItemTags:              form.actionItemTags || null
            };

            const url    = editingId ? `/api/EndpointHealth/rules/${editingId}` : '/api/EndpointHealth/rules';
            const method = editingId ? 'PUT' : 'POST';
            const res    = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });

            if (res.ok) {
                toast.success(editingId ? 'Health rule updated' : 'Health rule created');
                closeModal();
                fetchAll();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? 'Failed to save health rule');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await apiFetch(`/api/EndpointHealth/rules/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Rule deleted'); setDeleteConfirmId(null); fetchAll(); }
            else toast.error('Failed to delete rule');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await apiFetch(`/api/EndpointHealth/rules/${id}/toggle`, { method: 'PATCH' });
            if (res.ok) fetchAll();
            else toast.error('Failed to toggle rule');
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    if (loading) return <div className="module-template-config loading-state"><FaSpinner className="spin" /> Loading health rules&hellip;</div>;

    return (
        <div className="module-template-config">
            <div className="config-header">
                <div className="config-title">
                    <FaShieldAlt className="config-icon" />
                    <div>
                        <h2>Endpoint Health Rules</h2>
                        <p>Define threshold conditions that trigger IT Action Items when device metrics breach configured limits.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openCreate}><FaPlus /> New Rule</button>
            </div>

            {rules.length === 0 ? (
                <div className="empty-state">
                    <FaShieldAlt size={40} />
                    <p>No health rules configured yet.</p>
                    <p>Create a rule to automatically detect and alert on device health issues like low disk space, disabled Defender, or non-compliance.</p>
                </div>
            ) : (
                <table className="config-table">
                    <thead>
                        <tr>
                            <th>Rule Name</th>
                            <th>Metric</th>
                            <th>Condition</th>
                            <th>Priority</th>
                            <th>Auto-Remediate</th>
                            <th>Active</th>
                            <th style={{ width: 90 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map(r => (
                            <tr key={r.endpointHealthRuleId} style={{ opacity: r.isActive ? 1 : 0.5 }}>
                                <td>
                                    <strong>{r.name}</strong>
                                    {r.description && <div className="row-sub">{r.description}</div>}
                                </td>
                                <td>{METRIC_LABELS[r.metricType] ?? r.metricType}</td>
                                <td>
                                    <code style={{ fontSize: '0.78rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                        {OPERATOR_LABELS[r.operator] ?? r.operator} {r.thresholdValue}
                                        {r.secondaryThresholdValue ? ` – ${r.secondaryThresholdValue}` : ''}
                                    </code>
                                </td>
                                <td>
                                    <span style={{ fontWeight: 600, color: PRIORITY_COLOURS[r.alertPriority] ?? '#6b7280' }}>
                                        {r.alertPriority}
                                    </span>
                                </td>
                                <td>
                                    {r.autoRemediationEnabled
                                        ? <span style={{ color: '#f63040', fontWeight: 600 }}><FaRobot style={{ marginRight: 4 }} />{r.autoExecuteRemediation ? 'Auto' : 'Manual'}</span>
                                        : <span style={{ color: '#9ca3af' }}>—</span>}
                                </td>
                                <td>
                                    <button
                                        className="btn-icon"
                                        title={r.isActive ? 'Disable rule' : 'Enable rule'}
                                        onClick={() => handleToggle(r.endpointHealthRuleId)}
                                        style={{ color: r.isActive ? '#f63040' : '#9ca3af' }}
                                    >
                                        {r.isActive ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                    </button>
                                </td>
                                <td className="actions-cell">
                                    <button className="btn-icon" title="Edit" onClick={() => openEdit(r)}><FaEdit /></button>
                                    {deleteConfirmId === r.endpointHealthRuleId ? (
                                        <>
                                            <button className="btn-icon btn-danger" title="Confirm delete" onClick={() => handleDelete(r.endpointHealthRuleId)}><FaExclamationTriangle /></button>
                                            <button className="btn-icon" title="Cancel" onClick={() => setDeleteConfirmId(null)}><FaTimes /></button>
                                        </>
                                    ) : (
                                        <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteConfirmId(r.endpointHealthRuleId)}><FaTrashAlt /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-panel" style={{ maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit Health Rule' : 'New Health Rule'}</h3>
                            <button className="btn-icon" onClick={closeModal}><FaTimes /></button>
                        </div>
                        <form className="modal-body" onSubmit={handleSave}>

                            {/* Name & Description */}
                            <div className="form-group">
                                <label htmlFor="hr-name">Rule Name <span className="required">*</span></label>
                                <input id="hr-name" name="name" type="text" className="form-control" value={form.name} onChange={handleChange} placeholder="e.g. Low Disk Space – Warning" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="hr-desc">Description</label>
                                <textarea id="hr-desc" name="description" className="form-control" value={form.description} onChange={handleChange} rows={2} placeholder="Optional — what does this rule detect?" />
                            </div>

                            {/* Threshold — adaptive based on metric kind */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Threshold Condition</legend>

                                {/* Metric selector — always shown */}
                                <div className="form-group">
                                    <label htmlFor="hr-metric">Metric <span className="required">*</span></label>
                                    <select id="hr-metric" name="metricType" className="form-control" value={form.metricType} onChange={handleChange} required>
                                        {(metricTypes.length ? metricTypes : Object.keys(METRIC_LABELS)).map(m => (
                                            <option key={m} value={m}>{METRIC_LABELS[m] ?? m}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* ── Boolean metric: simple Yes / No toggle ── */}
                                {getMetricKind(form.metricType) === 'boolean' && (() => {
                                    // For security-check metrics the "bad" state is false (disabled)
                                    const invertedMetrics = new Set(['IsEncrypted', 'DefenderEnabled', 'RealTimeProtectionEnabled', 'FirewallEnabled', 'DefenderUpToDate']);
                                    const isInverted = invertedMetrics.has(form.metricType);
                                    return (
                                        <div style={{ marginTop: 8 }}>
                                            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Alert when this metric is&hellip;
                                            </label>
                                            <div style={{ display: 'flex', gap: 10 }}>
                                                {[
                                                    { val: 'false', label: isInverted ? '🔴 Disabled / false (recommended)' : '✗ False / Not detected' },
                                                    { val: 'true',  label: isInverted ? '🟢 Enabled / true'                  : '✓ True / Detected (recommended)' }
                                                ].map(opt => (
                                                    <label
                                                        key={opt.val}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: 8,
                                                            padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                                                            border: `2px solid ${form.thresholdValue === opt.val ? '#f63040' : '#e5e7eb'}`,
                                                            background: form.thresholdValue === opt.val ? '#fff1f2' : '#fafafa',
                                                            fontWeight: form.thresholdValue === opt.val ? 600 : 400,
                                                            flex: 1
                                                        }}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="thresholdValue"
                                                            value={opt.val}
                                                            checked={form.thresholdValue === opt.val}
                                                            onChange={handleChange}
                                                            style={{ accentColor: '#f63040' }}
                                                        />
                                                        {opt.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* ── Enum metric: operator (= / ≠) + value dropdown ── */}
                                {getMetricKind(form.metricType) === 'enum' && (
                                    <div className="form-row" style={{ marginTop: 8 }}>
                                        <div className="form-group">
                                            <label htmlFor="hr-op">Condition</label>
                                            <select id="hr-op" name="operator" className="form-control" value={form.operator} onChange={handleChange}>
                                                <option value="Equals">= Is</option>
                                                <option value="NotEquals">≠ Is not</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="hr-threshold">Value <span className="required">*</span></label>
                                            <select id="hr-threshold" name="thresholdValue" className="form-control" value={form.thresholdValue} onChange={handleChange} required>
                                                {(ENUM_METRICS[form.metricType] ?? []).map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* ── Numeric metric: operator + number input + optional range ── */}
                                {getMetricKind(form.metricType) === 'numeric' && (
                                    <>
                                        <div className="form-row" style={{ marginTop: 8 }}>
                                            <div className="form-group">
                                                <label htmlFor="hr-op">Operator <span className="required">*</span></label>
                                                <select id="hr-op" name="operator" className="form-control" value={form.operator} onChange={handleChange} required>
                                                    {(operators.length ? operators : Object.keys(OPERATOR_LABELS)).map(op => (
                                                        <option key={op} value={op}>{OPERATOR_LABELS[op] ?? op} — {op}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="hr-threshold">
                                                    Threshold Value <span className="required">*</span>
                                                    {form.metricType === 'StorageUsedPercent' && <span style={{ color: '#6b7280', fontWeight: 400 }}> (0–100 %)</span>}
                                                    {form.metricType === 'BatteryChargePercent' && <span style={{ color: '#6b7280', fontWeight: 400 }}> (0–100 %)</span>}
                                                    {(form.metricType.startsWith('DaysSince') || form.metricType === 'CertExpiryDays') && <span style={{ color: '#6b7280', fontWeight: 400 }}> (days)</span>}
                                                </label>
                                                <input
                                                    id="hr-threshold"
                                                    name="thresholdValue"
                                                    type="number"
                                                    className="form-control"
                                                    value={form.thresholdValue}
                                                    onChange={handleChange}
                                                    placeholder="e.g. 90"
                                                    min={0}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="hr-threshold2">Secondary Threshold <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional — creates a range condition)</span></label>
                                            <input
                                                id="hr-threshold2"
                                                name="secondaryThresholdValue"
                                                type="number"
                                                className="form-control"
                                                value={form.secondaryThresholdValue}
                                                onChange={handleChange}
                                                placeholder="e.g. 95 to alert between 90–95 %"
                                                min={0}
                                            />
                                        </div>
                                    </>
                                )}

                                {getMetricKind(form.metricType) === 'version' && (
                                    <div className="form-row" style={{ marginTop: 8 }}>
                                        <div className="form-group">
                                            <label htmlFor="hr-op">Operator <span className="required">*</span></label>
                                            <select id="hr-op" name="operator" className="form-control" value={form.operator} onChange={handleChange} required>
                                                {(operators.length ? operators : Object.keys(OPERATOR_LABELS)).map(op => (
                                                    <option key={op} value={op}>{OPERATOR_LABELS[op] ?? op} — {op}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="hr-threshold">
                                                Baseline OS Version <span className="required">*</span>
                                                <span style={{ color: '#6b7280', fontWeight: 400 }}> (e.g. 10.0.19045)</span>
                                            </label>
                                            <input
                                                id="hr-threshold"
                                                name="thresholdValue"
                                                type="text"
                                                className="form-control"
                                                value={form.thresholdValue}
                                                onChange={handleChange}
                                                placeholder="e.g. 10.0.19045"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Live condition summary */}
                                {form.thresholdValue !== '' && (
                                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.83rem', color: '#374151' }}>
                                        {describeCondition(form.metricType, form.operator, form.thresholdValue, form.secondaryThresholdValue)}
                                    </div>
                                )}
                            </fieldset>

                            {/* Alert Behaviour */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Alert Behaviour</legend>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="hr-cooldown">Cooldown (minutes)</label>
                                        <input id="hr-cooldown" name="cooldownMinutes" type="number" className="form-control" value={form.cooldownMinutes} onChange={handleChange} min={0} />
                                        <small>Suppresses duplicate alerts per device. Default 1440 (24 h).</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="hr-consecutive">Consecutive Breaches Required</label>
                                        <input id="hr-consecutive" name="consecutiveBreachesRequired" type="number" className="form-control" value={form.consecutiveBreachesRequired} onChange={handleChange} min={1} />
                                        <small>Check cycles that must breach before alerting. Default 1.</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="hr-os">OS Filter</label>
                                        <input id="hr-os" name="operatingSystemFilter" type="text" className="form-control" value={form.operatingSystemFilter} onChange={handleChange} placeholder="e.g. Windows or Windows,macOS" />
                                        <small>Comma-separated. Leave blank for all.</small>
                                    </div>
                                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 20 }}>
                                            <input type="checkbox" name="autoResolveOnRecovery" checked={form.autoResolveOnRecovery} onChange={handleChange} />
                                            Auto-resolve Action Item on recovery
                                        </label>
                                    </div>
                                </div>
                            </fieldset>

                            {/* Action Item Routing */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Action Item Routing</legend>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="hr-priority">Priority</label>
                                        <select id="hr-priority" name="alertPriority" className="form-control" value={form.alertPriority} onChange={handleChange}>
                                            {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="hr-template">Action Item Template</label>
                                        <select id="hr-template" name="templateKey" className="form-control" value={form.templateKey} onChange={handleChange}>
                                            <option value="">— None (use default) —</option>
                                            {templates.map(t => (
                                                <option key={t.templateKey} value={t.templateKey}>
                                                    {t.displayName}{t.department ? ` (${t.department})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <small>Template applied to generated action items for field defaults and routing.</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="hr-board">Kanban Board</label>
                                        <select id="hr-board" name="overrideBoardId" className="form-control" value={form.overrideBoardId} onChange={handleChange}>
                                            <option value="">— Auto (resolved via template) —</option>
                                            {boards.map(b => (
                                                <option key={b.boardId} value={b.boardId}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                        <small>Board where generated action items will be placed. Leave blank to auto-resolve from template.</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="hr-tags">Action Item Tags</label>
                                        <input id="hr-tags" name="actionItemTags" type="text" className="form-control" value={form.actionItemTags} onChange={handleChange} placeholder="e.g. UEM,AutoDetected,Storage" />
                                        <small>Comma-separated tags applied to generated action items.</small>
                                    </div>
                                </div>
                            </fieldset>

                            {/* Auto-Remediation */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Auto-Remediation</legend>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                                    <input type="checkbox" name="autoRemediationEnabled" checked={form.autoRemediationEnabled} onChange={handleChange} />
                                    Enable auto-remediation for this rule
                                </label>
                                {form.autoRemediationEnabled && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="hr-wizard">Remediation Wizard</label>
                                                <select id="hr-wizard" name="remediationWizardId" className="form-control" value={form.remediationWizardId} onChange={handleChange}>
                                                    <option value="">— None / Action Item only —</option>
                                                    {wizards.map(w => (
                                                        <option key={w.endpointRemediationWizardId} value={w.endpointRemediationWizardId}>
                                                            {w.name} ({w.category})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 20 }}>
                                                    <input type="checkbox" name="autoExecuteRemediation" checked={form.autoExecuteRemediation} onChange={handleChange} />
                                                    Auto-execute without confirmation
                                                </label>
                                                <small>When unchecked, wizard is attached to the action item for manual execution by IT staff.</small>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </fieldset>

                            {/* Control */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="hr-evalPriority">Evaluation Priority</label>
                                    <input id="hr-evalPriority" name="priority" type="number" className="form-control" value={form.priority} onChange={handleChange} min={1} />
                                    <small>Lower = evaluated first. Higher-priority rules suppress lower ones for the same metric.</small>
                                </div>
                                <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 20 }}>
                                        <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                        Rule is active
                                    </label>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? <><FaSpinner className="spin" /> Saving&hellip;</> : <><FaSave /> {editingId ? 'Update Rule' : 'Create Rule'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Tab: Remediation Wizards ─────────────────────────────────────────────────

const RemediationWizardsTab = ({ apiFetch }) => {
    const [wizards, setWizards]   = useState([]);
    const [boards, setBoards]     = useState([]);
    const [templates, setTemplates] = useState([]);
    const [scripts, setScripts]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [showModal, setShowModal]     = useState(false);
    const [editingId, setEditingId]     = useState(null);
    const [form, setForm]               = useState(emptyWizard());
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // Dry-run preview state (consumes GET /api/EndpointHealth/wizards/{id}/preview).
    const [preview, setPreview]             = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError]   = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [wizRes, boardsRes, templatesRes, scriptsRes] = await Promise.all([
                apiFetch('/api/EndpointHealth/wizards'),
                apiFetch('/api/KanbanBoards'),
                apiFetch('/api/Templates'),
                apiFetch('/api/EndpointHealth/scripts?activeOnly=true')
            ]);
            if (wizRes.ok) setWizards(await wizRes.json());
            if (boardsRes.ok) setBoards(await readWrappedCollection(boardsRes, 'boards'));
            if (templatesRes.ok) setTemplates(await readWrappedCollection(templatesRes, 'templates'));
            if (scriptsRes.ok) setScripts(await scriptsRes.json());
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load remediation wizards');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { fetchAll(); }, [fetchAll]);

    const resetPreview = () => { setPreview(null); setPreviewError(null); setPreviewLoading(false); };

    const openCreate = () => { setEditingId(null); setForm(emptyWizard()); resetPreview(); setShowModal(true); };
    const openEdit   = (w) => {
        setEditingId(w.endpointRemediationWizardId);
        resetPreview();
        setForm({
            name:                       w.name ?? '',
            description:                w.description ?? '',
            category:                   w.category ?? 'Storage',
            icon:                       w.icon ?? '',
            colour:                     w.colour ?? '#f63040',
            supportsAutoExecution:      w.supportsAutoExecution ?? false,
            maxAutoExecutionsPerDay:    w.maxAutoExecutionsPerDay ?? 3,
            estimatedDurationMinutes:   w.estimatedDurationMinutes ?? 5,
            timeoutMinutes:             w.timeoutMinutes ?? 30,
            supportedOperatingSystems:  w.supportedOperatingSystems ?? 'Windows',
            requiresDeviceOnline:       w.requiresDeviceOnline ?? true,
            requiresElevation:          w.requiresElevation ?? false,
            riskLevel:                  w.riskLevel ?? 'Low',
            isActive:                   w.isActive ?? true,
            sortOrder:                  w.sortOrder ?? 0,
            actionItemTemplateKey:      w.actionItemTemplateKey ?? '',
            actionItemBoardId:          w.actionItemBoardId ?? '',
            steps:                      Array.isArray(w.steps)
                ? [...w.steps]
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                    .map((s, i) => ({
                        endpointRemediationStepId: s.endpointRemediationStepId ?? 0,
                        name:                   s.name ?? '',
                        description:            s.description ?? '',
                        sortOrder:              i,
                        actionType:             s.actionType ?? 'RunScript',
                        actionConfigJson:       s.actionConfigJson ?? null,
                        continueOnFailure:      s.continueOnFailure ?? false,
                        timeoutSeconds:         s.timeoutSeconds ?? 300,
                        retryCount:             s.retryCount ?? 0,
                        retryDelaySeconds:      s.retryDelaySeconds ?? 30,
                        executionConditionJson: s.executionConditionJson ?? null,
                        icon:                   s.icon ?? '',
                        isActive:               s.isActive ?? true
                    }))
                : []
        });
        setShowModal(true);
    };
    const closeModal = () => { setShowModal(false); setEditingId(null); setForm(emptyWizard()); resetPreview(); };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleStepsChange = (nextSteps) => {
        setForm(prev => ({ ...prev, steps: nextSteps }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Wizard name is required'); return; }

        setSaving(true);
        try {
            const payload = {
                ...form,
                maxAutoExecutionsPerDay:    parseInt(form.maxAutoExecutionsPerDay, 10) || 3,
                estimatedDurationMinutes:   parseInt(form.estimatedDurationMinutes, 10) || 5,
                timeoutMinutes:             parseInt(form.timeoutMinutes, 10) || 30,
                sortOrder:                  parseInt(form.sortOrder, 10) || 0,
                description:                form.description || null,
                icon:                       form.icon || null,
                supportedOperatingSystems:  form.supportedOperatingSystems || null,
                actionItemTemplateKey:      form.actionItemTemplateKey || null,
                actionItemBoardId:          form.actionItemBoardId !== '' ? parseInt(form.actionItemBoardId, 10) : null,
                steps: (form.steps || []).map((s, i) => ({
                    name:                   s.name?.trim() || `Step ${i + 1}`,
                    description:            s.description || null,
                    sortOrder:              i,
                    actionType:             s.actionType,
                    actionConfigJson:       s.actionConfigJson || null,
                    continueOnFailure:      !!s.continueOnFailure,
                    timeoutSeconds:         parseInt(s.timeoutSeconds, 10) || 0,
                    retryCount:             parseInt(s.retryCount, 10) || 0,
                    retryDelaySeconds:      parseInt(s.retryDelaySeconds, 10) || 0,
                    executionConditionJson: s.executionConditionJson || null,
                    icon:                   s.icon || null,
                    isActive:               s.isActive !== false
                }))
            };

            const url    = editingId ? `/api/EndpointHealth/wizards/${editingId}` : '/api/EndpointHealth/wizards';
            const method = editingId ? 'PUT' : 'POST';
            const res    = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });

            if (res.ok) {
                toast.success(editingId ? 'Wizard updated' : 'Wizard created');
                closeModal();
                fetchAll();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? 'Failed to save wizard');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await apiFetch(`/api/EndpointHealth/wizards/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Wizard deleted'); setDeleteConfirmId(null); fetchAll(); }
            else toast.error('Failed to delete wizard');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        }
    };

    // Fetch a read-only dry-run for the wizard currently being edited. Requires the
    // wizard to be saved first so the backend has persisted steps to describe.
    const handlePreview = async () => {
        if (!editingId) {
            toast.info('Save the wizard first to preview exactly what it will run.');
            return;
        }
        setPreviewLoading(true);
        setPreviewError(null);
        setPreview(null);
        try {
            const res = await apiFetch(`/api/EndpointHealth/wizards/${editingId}/preview`);
            if (res.ok) {
                setPreview(await res.json());
            } else {
                setPreviewError('Failed to build preview for this wizard.');
            }
        } catch (err) {
            Sentry.captureException(err);
            setPreviewError('Unexpected error building preview.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleToggleActive = async (id) => {
        try {
            const res = await apiFetch(`/api/EndpointHealth/wizards/${id}/toggle`, { method: 'PATCH' });
            if (res.ok) fetchAll();
            else toast.error('Failed to toggle wizard');
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const handleToggleAuto = async (id, riskLevel) => {
        if (riskLevel === 'High') {
            toast.error('High-risk wizards always require manual confirmation and cannot be auto-executed.');
            return;
        }
        try {
            const res = await apiFetch(`/api/EndpointHealth/wizards/${id}/toggle-auto`, { method: 'PATCH' });
            if (res.ok) fetchAll();
            else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? 'Failed to toggle auto-execution');
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    if (loading) return <div className="module-template-config loading-state"><FaSpinner className="spin" /> Loading wizards&hellip;</div>;

    return (
        <div className="module-template-config">
            <div className="config-header">
                <div className="config-title">
                    <FaMagic className="config-icon" />
                    <div>
                        <h2>Remediation Wizards</h2>
                        <p>Pre-built auto-fix procedures that execute when a health rule fires. Link wizards to rules on the Health Rules tab.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openCreate}><FaPlus /> New Wizard</button>
            </div>

            {wizards.length === 0 ? (
                <div className="empty-state">
                    <FaMagic size={40} />
                    <p>No remediation wizards configured yet.</p>
                    <p>Create a wizard to define the steps IT should take (or that run automatically) when a health issue is detected.</p>
                </div>
            ) : (
                <table className="config-table">
                    <thead>
                        <tr>
                            <th>Wizard Name</th>
                            <th>Category</th>
                            <th>OS</th>
                            <th>Risk</th>
                            <th>Steps</th>
                            <th>Auto-Execute</th>
                            <th>Active</th>
                            <th style={{ width: 80 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {wizards.map(w => (
                            <tr key={w.endpointRemediationWizardId} style={{ opacity: w.isActive ? 1 : 0.5 }}>
                                <td>
                                    <strong>{w.name}</strong>
                                    {w.description && <div className="row-sub">{w.description}</div>}
                                </td>
                                <td>{w.category}</td>
                                <td>{w.supportedOperatingSystems ?? <span style={{ color: '#9ca3af' }}>All</span>}</td>
                                <td>
                                    <span style={{ color: w.riskLevel === 'High' ? '#ef4444' : w.riskLevel === 'Medium' ? '#f59e0b' : '#6b7280', fontWeight: 600 }}>
                                        {w.riskLevel}
                                    </span>
                                </td>
                                <td>{w.stepCount ?? '—'}</td>
                                <td>
                                    <button
                                        className="btn-icon"
                                        title={w.supportsAutoExecution ? 'Disable auto-execution' : (w.riskLevel === 'High' ? 'High-risk: manual only' : 'Enable auto-execution')}
                                        onClick={() => handleToggleAuto(w.endpointRemediationWizardId, w.riskLevel)}
                                        style={{ color: w.supportsAutoExecution ? '#f63040' : '#9ca3af', cursor: w.riskLevel === 'High' ? 'not-allowed' : 'pointer' }}
                                        disabled={w.riskLevel === 'High'}
                                    >
                                        {w.supportsAutoExecution ? <FaBell size={16} /> : <FaBellSlash size={16} />}
                                    </button>
                                </td>
                                <td>
                                    <button
                                        className="btn-icon"
                                        title={w.isActive ? 'Deactivate wizard' : 'Activate wizard'}
                                        onClick={() => handleToggleActive(w.endpointRemediationWizardId)}
                                        style={{ color: w.isActive ? '#f63040' : '#9ca3af' }}
                                    >
                                        {w.isActive ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                    </button>
                                </td>
                                <td className="actions-cell">
                                    <button className="btn-icon" title="Edit" onClick={() => openEdit(w)}><FaEdit /></button>
                                    {!w.isSystemWizard && (
                                        deleteConfirmId === w.endpointRemediationWizardId ? (
                                            <>
                                                <button className="btn-icon btn-danger" title="Confirm delete" onClick={() => handleDelete(w.endpointRemediationWizardId)}><FaExclamationTriangle /></button>
                                                <button className="btn-icon" title="Cancel" onClick={() => setDeleteConfirmId(null)}><FaTimes /></button>
                                            </>
                                        ) : (
                                            <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteConfirmId(w.endpointRemediationWizardId)}><FaTrashAlt /></button>
                                        )
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-panel" style={{ maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit Remediation Wizard' : 'New Remediation Wizard'}</h3>
                            <button className="btn-icon" onClick={closeModal}><FaTimes /></button>
                        </div>
                        <form className="modal-body" onSubmit={handleSave}>

                            <div className="form-group">
                                <label htmlFor="wiz-name">Wizard Name <span className="required">*</span></label>
                                <input id="wiz-name" name="name" type="text" className="form-control" value={form.name} onChange={handleChange} placeholder="e.g. Disk Cleanup Wizard" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="wiz-desc">Description</label>
                                <textarea id="wiz-desc" name="description" className="form-control" value={form.description} onChange={handleChange} rows={2} placeholder="What does this wizard do?" />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="wiz-cat">Category <span className="required">*</span></label>
                                    <select id="wiz-cat" name="category" className="form-control" value={form.category} onChange={handleChange}>
                                        {REMEDIATION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="wiz-risk">Risk Level</label>
                                    <select id="wiz-risk" name="riskLevel" className="form-control" value={form.riskLevel} onChange={handleChange}>
                                        {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <small>High-risk wizards always require manual confirmation.</small>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="wiz-os">Supported OS</label>
                                    <input id="wiz-os" name="supportedOperatingSystems" type="text" className="form-control" value={form.supportedOperatingSystems} onChange={handleChange} placeholder="e.g. Windows or Windows,macOS" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="wiz-colour">Colour</label>
                                    <input id="wiz-colour" name="colour" type="color" className="form-control" value={form.colour} onChange={handleChange} style={{ height: 38 }} />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="wiz-est">Estimated Duration (min)</label>
                                    <input id="wiz-est" name="estimatedDurationMinutes" type="number" className="form-control" value={form.estimatedDurationMinutes} onChange={handleChange} min={1} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="wiz-timeout">Timeout (min)</label>
                                    <input id="wiz-timeout" name="timeoutMinutes" type="number" className="form-control" value={form.timeoutMinutes} onChange={handleChange} min={1} />
                                </div>
                            </div>

                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Execution Options</legend>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                                    <input type="checkbox" name="supportsAutoExecution" checked={form.supportsAutoExecution} onChange={handleChange} />
                                    Supports auto-execution (can run without IT confirmation)
                                </label>
                                {form.supportsAutoExecution && (
                                    <div className="form-group">
                                        <label htmlFor="wiz-maxauto">Max Auto-Executions Per Day</label>
                                        <input id="wiz-maxauto" name="maxAutoExecutionsPerDay" type="number" className="form-control" value={form.maxAutoExecutionsPerDay} onChange={handleChange} min={1} max={50} />
                                        <small>Safety cap to prevent runaway remediation loops.</small>
                                    </div>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                                    <input type="checkbox" name="requiresDeviceOnline" checked={form.requiresDeviceOnline} onChange={handleChange} />
                                    Requires device to be online
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input type="checkbox" name="requiresElevation" checked={form.requiresElevation} onChange={handleChange} />
                                    Requires elevated / admin permissions
                                </label>
                            </fieldset>

                            {/* Action Item Configuration */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Action Item Configuration</legend>
                                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 12 }}>Configure which Action Item template and Kanban board to use when this wizard creates or escalates action items.</p>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="wiz-template">Action Item Template</label>
                                        <select id="wiz-template" name="actionItemTemplateKey" className="form-control" value={form.actionItemTemplateKey} onChange={handleChange}>
                                            <option value="">— None (use default) —</option>
                                            {templates.map(t => (
                                                <option key={t.templateKey} value={t.templateKey}>
                                                    {t.displayName}{t.department ? ` (${t.department})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <small>Template applied when this wizard creates follow-up action items.</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="wiz-board">Kanban Board</label>
                                        <select id="wiz-board" name="actionItemBoardId" className="form-control" value={form.actionItemBoardId} onChange={handleChange}>
                                            <option value="">— Auto (resolved via template) —</option>
                                            {boards.map(b => (
                                                <option key={b.boardId} value={b.boardId}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                        <small>Board where action items from this wizard will be placed.</small>
                                    </div>
                                </div>
                            </fieldset>

                            {/* Wizard Steps — define exactly what this wizard runs */}
                            <RemediationStepEditor
                                steps={form.steps}
                                onChange={handleStepsChange}
                                scripts={scripts}
                            />

                            {/* Dry-Run Preview — show what the wizard will run in this environment */}
                            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                                <button type="button" className="btn-secondary" onClick={handlePreview} disabled={previewLoading}>
                                    {previewLoading ? <><FaSpinner className="spin" /> Building preview&hellip;</> : <><FaSearch /> Preview Dry-Run</>}
                                </button>
                                {!editingId && (
                                    <small style={{ alignSelf: 'center', marginLeft: 10, color: '#9ca3af' }}>
                                        Save the wizard first to preview exactly what it will run.
                                    </small>
                                )}
                            </div>
                            {(preview || previewLoading || previewError) && (
                                <WizardPreviewPanel
                                    preview={preview}
                                    loading={previewLoading}
                                    error={previewError}
                                    onClose={resetPreview}
                                />
                            )}

                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, marginTop: 16 }}>
                                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                Wizard is active
                            </label>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? <><FaSpinner className="spin" /> Saving&hellip;</> : <><FaSave /> {editingId ? 'Update Wizard' : 'Create Wizard'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Tab: Alert History ───────────────────────────────────────────────────────

const AlertHistoryTab = ({ apiFetch }) => {
    const [alerts, setAlerts]   = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage]       = useState(1);
    const [filter, setFilter]   = useState({ isActive: '', metricType: '' });
    const [loading, setLoading] = useState(true);
    const [resolvingId, setResolvingId] = useState(null);

    const PAGE_SIZE = 25;

    const fetchAlerts = useCallback(async (pg = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: pg, pageSize: PAGE_SIZE });
            if (filter.isActive !== '')    params.set('isActive', filter.isActive);
            if (filter.metricType !== '')  params.set('metricType', filter.metricType);

            const res = await apiFetch(`/api/EndpointHealth/alerts?${params}`);
            if (res.ok) {
                const data = await res.json();
                setAlerts(data.alerts ?? []);
                setTotalCount(data.totalCount ?? 0);
                setPage(pg);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load alerts');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, filter]);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { fetchAlerts(1); }, [fetchAlerts]);

    const handleResolve = async (id) => {
        setResolvingId(id);
        try {
            const res = await apiFetch(`/api/EndpointHealth/alerts/${id}/resolve`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ resolutionMethod: 'ManualResolution' })
            });
            if (res.ok) { toast.success('Alert resolved'); fetchAlerts(page); }
            else toast.error('Failed to resolve alert');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        } finally {
            setResolvingId(null);
        }
    };

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    return (
        <div className="module-template-config">
            <div className="config-header">
                <div className="config-title">
                    <FaHistory className="config-icon" />
                    <div>
                        <h2>Alert History</h2>
                        <p>View active and resolved endpoint health alerts. Manually resolve active alerts when needed.</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                        className="form-control"
                        style={{ width: 140 }}
                        value={filter.isActive}
                        onChange={e => setFilter(f => ({ ...f, isActive: e.target.value }))}
                    >
                        <option value="">All alerts</option>
                        <option value="true">Active only</option>
                        <option value="false">Resolved only</option>
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 180 }}
                        value={filter.metricType}
                        onChange={e => setFilter(f => ({ ...f, metricType: e.target.value }))}
                    >
                        <option value="">All metric types</option>
                        {Object.entries(METRIC_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-state"><FaSpinner className="spin" /> Loading alerts&hellip;</div>
            ) : alerts.length === 0 ? (
                <div className="empty-state">
                    <FaHistory size={40} />
                    <p>No alerts match the current filter.</p>
                </div>
            ) : (
                <>
                    <table className="config-table">
                        <thead>
                            <tr>
                                <th>Device</th>
                                <th>Metric</th>
                                <th>Value / Threshold</th>
                                <th>Status</th>
                                <th>Detected</th>
                                <th>Resolved</th>
                                <th style={{ width: 80 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {alerts.map(a => (
                                <tr key={a.endpointHealthAlertId}>
                                    <td>
                                        <strong>{a.deviceName ?? a.deviceId}</strong>
                                        {a.userDisplayName && <div className="row-sub">{a.userDisplayName}</div>}
                                    </td>
                                    <td>{METRIC_LABELS[a.metricType] ?? a.metricType}</td>
                                    <td>
                                        <code style={{ fontSize: '0.78rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                            {a.triggerValue} / {a.thresholdValue}
                                        </code>
                                    </td>
                                    <td>
                                        {a.isActive
                                            ? <span style={{ color: '#ef4444', fontWeight: 600 }}>● Active</span>
                                            : <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Resolved</span>}
                                    </td>
                                    <td style={{ fontSize: '0.82rem' }}>{new Date(a.createdDate).toLocaleString()}</td>
                                    <td style={{ fontSize: '0.82rem' }}>
                                        {a.resolvedDate ? new Date(a.resolvedDate).toLocaleString() : '—'}
                                    </td>
                                    <td className="actions-cell">
                                        {a.isActive && (
                                            <button
                                                className="btn-icon"
                                                title="Resolve alert"
                                                onClick={() => handleResolve(a.endpointHealthAlertId)}
                                                disabled={resolvingId === a.endpointHealthAlertId}
                                            >
                                                {resolvingId === a.endpointHealthAlertId ? <FaSpinner className="spin" /> : <FaCheckCircle style={{ color: '#10b981' }} />}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                            <button className="btn-secondary" disabled={page <= 1} onClick={() => fetchAlerts(page - 1)}>← Previous</button>
                            <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: '#6b7280' }}>Page {page} of {totalPages} ({totalCount} total)</span>
                            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => fetchAlerts(page + 1)}>Next →</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ── Tab: System Monitoring ────────────────────────────────────────────────────

const SYSTEM_METRIC_LABELS = {
    // Exchange
    MailboxUsagePercent: 'Mailbox Usage %',
    MailQueueLength: 'Mail Queue Length',
    DailyEmailVolume: 'Daily Email Volume',
    SpamReceivedPercent: 'Spam Received %',
    MailboxInactivityDays: 'Mailbox Inactivity (days)',
    LargestMailboxSizeGB: 'Largest Mailbox Size (GB)',
    // SharePoint
    SiteStorageUsedPercent: 'Site Storage Used %',
    InactiveSiteCount: 'Inactive Sites (over threshold)',
    SiteCount: 'Total Sites',
    SitesOver1GBCount: 'Sites Over 1 GB',
    SiteStorageAllocatedGB: 'Site Storage Allocated (GB)',
    // Rubrik
    BackupSuccessRate: 'Backup Success Rate %',
    BackupFailedCount: 'Backup Failed Count',
    BackupMissedSLACount: 'Backup Missed SLA Count',
    BackupStorageUsedTB: 'Backup Storage Used (TB)',
    BackupAgeHours: 'Backup Age (hours)',
    ArchivalCompliancePercent: 'Archival Compliance %',
    // Proxmox
    ClusterCPUPercent: 'Cluster CPU %',
    ClusterMemoryPercent: 'Cluster Memory %',
    ClusterStoragePercent: 'Cluster Storage %',
    VMDownCount: 'VMs Down',
    NodeOfflineCount: 'Nodes Offline',
    HighAvailabilityFaults: 'HA Faults',
    // GitHub
    OpenVulnerabilityCount: 'Open Vulnerabilities',
    DependabotCriticalCount: 'Dependabot Critical Alerts',
    CodeScanningAlertCount: 'Code Scanning Alerts',
    SecretScanningAlertCount: 'Secret Scanning Alerts',
    StaleIssuesDays: 'Stale Issues (days)',
    PRReviewPendingDays: 'PR Review Pending (days)',
    OpenBugCount: 'Open Bug Count',
    // Network
    OutageCountMonth: 'Outages This Month',
    OutageTotalMinutes: 'Outage Total Minutes',
    RouterOfflineCount: 'Routers Offline',
    RouterUptimePercent: 'Router Uptime %',
    RouterFirmwareOutdatedCount: 'Routers Firmware Outdated',
    // Telephony
    MissedCallPercent: 'Missed Call %',
    AverageCallWaitSeconds: 'Avg Call Wait (seconds)',
    AbandonedCallPercent: 'Abandoned Call %',
    DailyCallVolume: 'Daily Call Volume',
    // Sentry
    UnresolvedIssueCount: 'Unresolved Issues',
    CrashFreeSessionsPercent: 'Crash-Free Sessions %',
    ErrorSpikePercent: 'Error Spike %',
    P95TransactionMs: 'P95 Transaction (ms)',
    // Security
    SecureScorePercent: 'Secure Score %',
    RiskySignInCount: 'Risky Sign-Ins',
    MFANotRegisteredPercent: 'MFA Not Registered %',
    ConditionalAccessFailures: 'Conditional Access Failures',
    IdentityRiskHighCount: 'High-Risk Identities',
    // Intune (Unified Endpoint Management)
    DeviceNonCompliantPercent: 'Devices Non-Compliant %',
    DeviceNonCompliantCount: 'Devices Non-Compliant (count)',
    ManagedDeviceCount: 'Managed Devices (count)',
    // Purview (Data Governance)
    DlpOpenAlertCount: 'DLP Open Alerts',
    DlpCriticalAlertCount: 'DLP Critical Alerts',
    DlpResolutionRatePercent: 'DLP Resolution Rate %',
    SensitivityLabelCoveragePercent: 'Sensitivity Label Coverage %'
};

const SYSTEM_CATEGORY_LABELS = {
    Exchange: '📧 Exchange Online',
    SharePoint: '📁 SharePoint',
    Rubrik: '💾 Rubrik Backup',
    Proxmox: '🖥️ Proxmox Cluster',
    GitHub: '🐙 GitHub',
    Network: '🌐 Network & Routers',
    Telephony: '📞 Telephony',
    Sentry: '🐛 Sentry (Production)',
    Security: '🔐 Security & Identity',
    Intune: '📱 Intune (Devices)',
    Purview: '🛡️ Purview (Compliance)'
};

const SYSTEM_CATEGORIES = Object.keys(SYSTEM_CATEGORY_LABELS);

const SYSTEM_METRICS_BY_CATEGORY = {
    Exchange: ['MailboxUsagePercent', 'MailQueueLength', 'DailyEmailVolume', 'SpamReceivedPercent', 'MailboxInactivityDays', 'LargestMailboxSizeGB'],
    SharePoint: ['SiteStorageUsedPercent', 'InactiveSiteCount', 'SiteCount', 'SitesOver1GBCount', 'SiteStorageAllocatedGB'],
    Rubrik: ['BackupSuccessRate', 'BackupFailedCount', 'BackupMissedSLACount', 'BackupStorageUsedTB', 'BackupAgeHours', 'ArchivalCompliancePercent'],
    Proxmox: ['ClusterCPUPercent', 'ClusterMemoryPercent', 'ClusterStoragePercent', 'VMDownCount', 'NodeOfflineCount', 'HighAvailabilityFaults'],
    GitHub: ['OpenVulnerabilityCount', 'DependabotCriticalCount', 'CodeScanningAlertCount', 'SecretScanningAlertCount', 'StaleIssuesDays', 'PRReviewPendingDays', 'OpenBugCount'],
    Network: ['OutageCountMonth', 'OutageTotalMinutes', 'RouterOfflineCount', 'RouterUptimePercent', 'RouterFirmwareOutdatedCount'],
    Telephony: ['MissedCallPercent', 'AverageCallWaitSeconds', 'AbandonedCallPercent', 'DailyCallVolume'],
    Sentry: ['UnresolvedIssueCount', 'CrashFreeSessionsPercent', 'ErrorSpikePercent', 'P95TransactionMs'],
    Security: ['SecureScorePercent', 'RiskySignInCount', 'MFANotRegisteredPercent', 'ConditionalAccessFailures', 'IdentityRiskHighCount'],
    Intune: ['DeviceNonCompliantPercent', 'DeviceNonCompliantCount', 'ManagedDeviceCount'],
    Purview: ['DlpOpenAlertCount', 'DlpCriticalAlertCount', 'DlpResolutionRatePercent', 'SensitivityLabelCoveragePercent']
};

const defaultSystemThreshold = (metric) => {
    const defaults = {
        MailboxUsagePercent: '90', MailQueueLength: '50', DailyEmailVolume: '10000',
        SpamReceivedPercent: '30', MailboxInactivityDays: '90', LargestMailboxSizeGB: '45',
        SiteStorageUsedPercent: '90', InactiveSiteCount: '50', SiteCount: '200',
        SitesOver1GBCount: '20', SiteStorageAllocatedGB: '100',
        BackupSuccessRate: '95', BackupFailedCount: '1', BackupMissedSLACount: '1',
        BackupStorageUsedTB: '50', BackupAgeHours: '26', ArchivalCompliancePercent: '95',
        ClusterCPUPercent: '85', ClusterMemoryPercent: '85', ClusterStoragePercent: '85',
        VMDownCount: '1', NodeOfflineCount: '1', HighAvailabilityFaults: '1',
        OpenVulnerabilityCount: '5', DependabotCriticalCount: '1', CodeScanningAlertCount: '3',
        SecretScanningAlertCount: '1', StaleIssuesDays: '30', PRReviewPendingDays: '7', OpenBugCount: '20',
        OutageCountMonth: '3', OutageTotalMinutes: '120', RouterOfflineCount: '1',
        RouterUptimePercent: '99', RouterFirmwareOutdatedCount: '3',
        MissedCallPercent: '20', AverageCallWaitSeconds: '60', AbandonedCallPercent: '15', DailyCallVolume: '500',
        UnresolvedIssueCount: '25', CrashFreeSessionsPercent: '99', ErrorSpikePercent: '200', P95TransactionMs: '3000',
        SecureScorePercent: '70', RiskySignInCount: '5', MFANotRegisteredPercent: '10',
        ConditionalAccessFailures: '20', IdentityRiskHighCount: '3',
        DeviceNonCompliantPercent: '20', DeviceNonCompliantCount: '10', ManagedDeviceCount: '1',
        DlpOpenAlertCount: '5', DlpCriticalAlertCount: '1', DlpResolutionRatePercent: '80', SensitivityLabelCoveragePercent: '60'
    };
    return defaults[metric] ?? '';
};

const defaultSystemOperator = (metric) => {
    // For "rate" or "percent" metrics where high is good, alert when it drops below
    const lowIsBad = new Set(['BackupSuccessRate', 'ArchivalCompliancePercent', 'RouterUptimePercent', 'CrashFreeSessionsPercent', 'SecureScorePercent', 'DlpResolutionRatePercent', 'SensitivityLabelCoveragePercent', 'ManagedDeviceCount']);
    return lowIsBad.has(metric) ? 'LessThanOrEqual' : 'GreaterThanOrEqual';
};

const emptySystemRule = (category) => ({
    name: '',
    description: '',
    category: category || 'Exchange',
    metricType: (SYSTEM_METRICS_BY_CATEGORY[category] ?? SYSTEM_METRICS_BY_CATEGORY.Exchange)[0],
    operator: 'GreaterThanOrEqual',
    thresholdValue: '',
    secondaryThresholdValue: '',
    cooldownMinutes: 1440,
    consecutiveBreachesRequired: 1,
    autoResolveOnRecovery: true,
    alertPriority: 'Medium',
    templateKey: '',
    assignToUserId: '',
    assignToDepartmentId: '',
    overrideBoardId: '',
    actionItemTags: '',
    isActive: false,
    priority: 100,
    sortOrder: 0,
    // Auto-remediation (opt-in self-healing). When enabled and a wizard is selected, a
    // breach can attach or auto-run the remediation wizard. Auto-execute is guarded server-side
    // (simulated outside Production) so it is always safe to enable.
    autoRemediationEnabled: false,
    remediationWizardId: '',
    autoExecuteRemediation: false
});

/**
 * Helper that safely parses an integer from a form value.
 * Unlike `parseInt(v) || fallback`, this correctly preserves 0 — it only
 * uses the fallback when parsing produces NaN (i.e. an empty or non-numeric string).
 */
const safeParseInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

const SystemMonitoringTab = ({ apiFetch }) => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('Exchange');
    const [form, setForm] = useState(emptySystemRule('Exchange'));
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [boards, setBoards] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [wizards, setWizards] = useState([]);

    // API-driven category and metric lists — keeps the client in sync with the backend
    // as new categories or metrics are added, without requiring a frontend deploy.
    // Initialized from local constants; updated with server data on load.
    // Falls back to the local SYSTEM_METRICS_BY_CATEGORY constants if the fetch fails.
    const [metricsByCategory, setMetricsByCategory] = useState(SYSTEM_METRICS_BY_CATEGORY);
    const [categories, setCategories] = useState(SYSTEM_CATEGORIES);

    const fetchRules = useCallback(async () => {
        try {
            const [rulesRes, boardsRes, templatesRes, metricsRes, categoriesRes, wizardsRes] = await Promise.all([
                apiFetch('/api/SystemHealth/rules'),
                apiFetch('/api/KanbanBoards'),
                apiFetch('/api/Templates'),
                apiFetch('/api/SystemHealth/metrics/supported-by-category'),
                apiFetch('/api/SystemHealth/categories'),
                apiFetch('/api/EndpointHealth/wizards')
            ]);
            if (rulesRes.ok) setRules(await rulesRes.json());
            if (boardsRes.ok) setBoards(await readWrappedCollection(boardsRes, 'boards'));
            if (templatesRes.ok) setTemplates(await readWrappedCollection(templatesRes, 'templates'));
            if (wizardsRes.ok) setWizards(await wizardsRes.json());
            // Use server-provided metric lists — filters to only supported/collected metrics
            if (metricsRes.ok) {
                const serverMetrics = await metricsRes.json();
                setMetricsByCategory(serverMetrics);
            }
            if (categoriesRes.ok) {
                const serverCats = await categoriesRes.json();
                setCategories(serverCats);
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load system health rules');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { fetchRules(); }, [fetchRules]);

    const filteredRules = rules.filter(r => r.category === selectedCategory);

    const openCreate = () => {
        setEditingId(null);
        const newForm = emptySystemRule(selectedCategory);
        // Use server metrics if available, fall back to local constant
        const firstMetric = (metricsByCategory[selectedCategory] ?? SYSTEM_METRICS_BY_CATEGORY[selectedCategory] ?? [])[0] ?? '';
        newForm.metricType = firstMetric;
        newForm.operator = defaultSystemOperator(firstMetric);
        newForm.thresholdValue = defaultSystemThreshold(firstMetric);
        setForm(newForm);
        setShowModal(true);
    };

    const openEdit = (r) => {
        setEditingId(r.systemHealthRuleId);
        setForm({
            name: r.name ?? '',
            description: r.description ?? '',
            category: r.category ?? 'Exchange',
            metricType: r.metricType ?? '',
            operator: r.operator ?? 'GreaterThanOrEqual',
            thresholdValue: r.thresholdValue ?? '',
            secondaryThresholdValue: r.secondaryThresholdValue ?? '',
            cooldownMinutes: r.cooldownMinutes ?? 1440,
            consecutiveBreachesRequired: r.consecutiveBreachesRequired ?? 1,
            autoResolveOnRecovery: r.autoResolveOnRecovery ?? true,
            alertPriority: r.alertPriority ?? 'Medium',
            templateKey: r.templateKey ?? '',
            assignToUserId: r.assignToUserId ?? '',
            assignToDepartmentId: r.assignToDepartmentId ?? '',
            overrideBoardId: r.overrideBoardId ?? '',
            actionItemTags: r.actionItemTags ?? '',
            autoRemediationEnabled: r.autoRemediationEnabled ?? false,
            remediationWizardId: r.remediationWizardId ?? '',
            autoExecuteRemediation: r.autoExecuteRemediation ?? false,
            isActive: r.isActive ?? false,
            priority: r.priority ?? 100,
            sortOrder: r.sortOrder ?? 0
        });
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setEditingId(null); };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'category') {
            // Use server-provided metric list for the selected category; fall back to local
            const metrics = metricsByCategory[value] ?? SYSTEM_METRICS_BY_CATEGORY[value] ?? [];
            const firstMetric = metrics[0] ?? '';
            setForm(prev => ({
                ...prev,
                category: value,
                metricType: firstMetric,
                operator: defaultSystemOperator(firstMetric),
                thresholdValue: defaultSystemThreshold(firstMetric),
                secondaryThresholdValue: ''
            }));
            return;
        }
        if (name === 'metricType') {
            setForm(prev => ({
                ...prev,
                metricType: value,
                operator: defaultSystemOperator(value),
                thresholdValue: defaultSystemThreshold(value),
                secondaryThresholdValue: ''
            }));
            return;
        }
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // Whether the currently-selected operator requires a secondary threshold (range operators)
    const isRangeOperator = form.operator === 'Between' || form.operator === 'NotBetween';

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Name is required'); return; }
        if (!form.thresholdValue.toString().trim()) { toast.error('Threshold value is required'); return; }
        if (isRangeOperator && !form.secondaryThresholdValue?.toString().trim()) {
            toast.error(`Operator '${form.operator}' requires a secondary threshold value`);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                // Use safeParseInt to preserve 0 — parseInt(v) || fallback would override 0 with the fallback
                cooldownMinutes: safeParseInt(form.cooldownMinutes, 1440),
                consecutiveBreachesRequired: safeParseInt(form.consecutiveBreachesRequired, 1),
                priority: safeParseInt(form.priority, 100),
                sortOrder: safeParseInt(form.sortOrder, 0),
                assignToUserId: form.assignToUserId !== '' ? safeParseInt(form.assignToUserId, null) : null,
                assignToDepartmentId: form.assignToDepartmentId !== '' ? safeParseInt(form.assignToDepartmentId, null) : null,
                overrideBoardId: form.overrideBoardId !== '' ? safeParseInt(form.overrideBoardId, null) : null,
                secondaryThresholdValue: form.secondaryThresholdValue || null,
                templateKey: form.templateKey || null,
                actionItemTags: form.actionItemTags || null,
                // Auto-remediation: only persist a wizard id when auto-remediation is enabled.
                autoRemediationEnabled: !!form.autoRemediationEnabled,
                remediationWizardId: form.autoRemediationEnabled && form.remediationWizardId !== ''
                    ? safeParseInt(form.remediationWizardId, null) : null,
                autoExecuteRemediation: !!form.autoRemediationEnabled && !!form.autoExecuteRemediation
            };

            const url = editingId ? `/api/SystemHealth/rules/${editingId}` : '/api/SystemHealth/rules';
            const method = editingId ? 'PUT' : 'POST';
            const res = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });

            if (res.ok) {
                toast.success(editingId ? 'System rule updated' : 'System rule created');
                closeModal();
                fetchRules();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.title ?? err.message ?? 'Failed to save system rule');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await apiFetch(`/api/SystemHealth/rules/${id}`, { method: 'DELETE' });
            if (res.ok) { toast.success('Rule deleted'); setDeleteConfirmId(null); fetchRules(); }
            else toast.error('Failed to delete rule');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Unexpected error');
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await apiFetch(`/api/SystemHealth/rules/${id}/toggle`, { method: 'PATCH' });
            if (res.ok) fetchRules();
            else toast.error('Failed to toggle rule');
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    if (loading) return <div className="module-template-config loading-state"><FaSpinner className="spin" /> Loading system monitoring rules&hellip;</div>;

    return (
        <div className="module-template-config">
            <div className="config-header">
                <div className="config-title">
                    <FaServer className="config-icon" />
                    <div>
                        <h2>System Monitoring Rules</h2>
                        <p>Define threshold conditions for M365 and infrastructure services. Rules are disabled by default — enable them when ready.</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openCreate}><FaPlus /> New Rule</button>
            </div>

            {/* Category selector */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                {categories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        style={{
                            padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                            background: selectedCategory === cat ? '#f63040' : '#f3f4f6',
                            color: selectedCategory === cat ? '#fff' : '#374151',
                            fontWeight: selectedCategory === cat ? 600 : 400,
                            fontSize: '0.82rem', transition: 'all 0.15s'
                        }}
                    >
                        {SYSTEM_CATEGORY_LABELS[cat] ?? cat}
                        {(() => { const count = rules.filter(r => r.category === cat).length; return count > 0 ? ` (${count})` : ''; })()}
                    </button>
                ))}
            </div>

            {filteredRules.length === 0 ? (
                <div className="empty-state">
                    <FaServer size={40} />
                    <p>No monitoring rules for {SYSTEM_CATEGORY_LABELS[selectedCategory]} yet.</p>
                    <p>Create a rule to get alerted when {selectedCategory} metrics breach your configured thresholds.</p>
                </div>
            ) : (
                <table className="config-table">
                    <thead>
                        <tr>
                            <th>Rule Name</th>
                            <th>Metric</th>
                            <th>Condition</th>
                            <th>Priority</th>
                            <th>Active</th>
                            <th style={{ width: 90 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRules.map(r => (
                            <tr key={r.systemHealthRuleId} style={{ opacity: r.isActive ? 1 : 0.5 }}>
                                <td>
                                    <strong>{r.name}</strong>
                                    {r.description && <div className="row-sub">{r.description}</div>}
                                </td>
                                <td>{SYSTEM_METRIC_LABELS[r.metricType] ?? r.metricType}</td>
                                <td>
                                    <code style={{ fontSize: '0.78rem', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                        {OPERATOR_LABELS[r.operator] ?? r.operator} {r.thresholdValue}
                                        {r.secondaryThresholdValue ? ` – ${r.secondaryThresholdValue}` : ''}
                                    </code>
                                </td>
                                <td>
                                    <span style={{ fontWeight: 600, color: PRIORITY_COLOURS[r.alertPriority] ?? '#6b7280' }}>
                                        {r.alertPriority}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="btn-icon"
                                        title={r.isActive ? 'Disable rule' : 'Enable rule'}
                                        onClick={() => handleToggle(r.systemHealthRuleId)}
                                        style={{ color: r.isActive ? '#f63040' : '#9ca3af' }}
                                    >
                                        {r.isActive ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                                    </button>
                                </td>
                                <td className="actions-cell">
                                    <button className="btn-icon" title="Edit" onClick={() => openEdit(r)}><FaEdit /></button>
                                    {deleteConfirmId === r.systemHealthRuleId ? (
                                        <>
                                            <button className="btn-icon btn-danger" title="Confirm delete" onClick={() => handleDelete(r.systemHealthRuleId)}><FaExclamationTriangle /></button>
                                            <button className="btn-icon" title="Cancel" onClick={() => setDeleteConfirmId(null)}><FaTimes /></button>
                                        </>
                                    ) : (
                                        <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteConfirmId(r.systemHealthRuleId)}><FaTrashAlt /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-panel" style={{ maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit System Rule' : 'New System Rule'}</h3>
                            <button className="btn-icon" onClick={closeModal}><FaTimes /></button>
                        </div>
                        <form className="modal-body" onSubmit={handleSave}>
                            <div className="form-group">
                                <label htmlFor="sr-name">Rule Name <span className="required">*</span></label>
                                <input id="sr-name" name="name" type="text" className="form-control" value={form.name} onChange={handleChange} placeholder="e.g. Exchange Mailbox Over 90%" required />
                            </div>
                            <div className="form-group">
                                <label htmlFor="sr-desc">Description</label>
                                <textarea id="sr-desc" name="description" className="form-control" value={form.description} onChange={handleChange} rows={2} placeholder="Optional — what does this rule detect?" />
                            </div>

                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Threshold Condition</legend>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sr-category">Category</label>
                                        <select id="sr-category" name="category" className="form-control" value={form.category} onChange={handleChange}>
                                            {categories.map(c => <option key={c} value={c}>{SYSTEM_CATEGORY_LABELS[c] ?? c}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="sr-metric">Metric <span className="required">*</span></label>
                                        <select id="sr-metric" name="metricType" className="form-control" value={form.metricType} onChange={handleChange} required>
                                            {(metricsByCategory[form.category] ?? SYSTEM_METRICS_BY_CATEGORY[form.category] ?? []).map(m => (
                                                <option key={m} value={m}>{SYSTEM_METRIC_LABELS[m] ?? m}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sr-op">Operator <span className="required">*</span></label>
                                        <select id="sr-op" name="operator" className="form-control" value={form.operator} onChange={handleChange} required>
                                            {Object.entries(OPERATOR_LABELS).map(([op, sym]) => (
                                                <option key={op} value={op}>{sym} — {op}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="sr-threshold">Threshold Value <span className="required">*</span></label>
                                        <input id="sr-threshold" name="thresholdValue" type="number" className="form-control" value={form.thresholdValue} onChange={handleChange} min={0} required />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="sr-threshold2">
                                        Secondary Threshold{' '}
                                        {isRangeOperator
                                            ? <span className="required">*</span>
                                            : <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional — required for Between / Not Between)</span>}
                                    </label>
                                    <input
                                        id="sr-threshold2"
                                        name="secondaryThresholdValue"
                                        type="number"
                                        className="form-control"
                                        value={form.secondaryThresholdValue}
                                        onChange={handleChange}
                                        min={0}
                                        required={isRangeOperator}
                                        placeholder={isRangeOperator ? 'Upper bound (required)' : 'Upper bound (optional)'}
                                    />
                                </div>
                            </fieldset>

                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Alert Behaviour</legend>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sr-cooldown">Cooldown (minutes)</label>
                                        <input id="sr-cooldown" name="cooldownMinutes" type="number" className="form-control" value={form.cooldownMinutes} onChange={handleChange} min={0} />
                                        <small>Suppresses duplicate alerts. Default 1440 (24 h).</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="sr-consecutive">Consecutive Breaches</label>
                                        <input id="sr-consecutive" name="consecutiveBreachesRequired" type="number" className="form-control" value={form.consecutiveBreachesRequired} onChange={handleChange} min={1} />
                                    </div>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 8 }}>
                                    <input type="checkbox" name="autoResolveOnRecovery" checked={form.autoResolveOnRecovery} onChange={handleChange} />
                                    Auto-resolve Action Item on recovery
                                </label>
                            </fieldset>

                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Action Item Routing</legend>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sr-priority">Priority</label>
                                        <select id="sr-priority" name="alertPriority" className="form-control" value={form.alertPriority} onChange={handleChange}>
                                            {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="sr-template">Action Item Template</label>
                                        <select id="sr-template" name="templateKey" className="form-control" value={form.templateKey} onChange={handleChange}>
                                            <option value="">— None (use default) —</option>
                                            {templates.map(t => (
                                                <option key={t.templateKey} value={t.templateKey}>
                                                    {t.displayName}{t.department ? ` (${t.department})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sr-board">Kanban Board</label>
                                        <select id="sr-board" name="overrideBoardId" className="form-control" value={form.overrideBoardId} onChange={handleChange}>
                                            <option value="">— Auto (resolved via template) —</option>
                                            {boards.map(b => <option key={b.boardId} value={b.boardId}>{b.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="sr-tags">Action Item Tags</label>
                                        <input id="sr-tags" name="actionItemTags" type="text" className="form-control" value={form.actionItemTags} onChange={handleChange} placeholder="e.g. SystemHealth,Exchange,AutoDetected" />
                                    </div>
                                </div>
                            </fieldset>

                            {/* Auto-Remediation — opt-in self-healing. Mirrors the Health Rules tab so the
                                experience is identical across endpoint and system monitoring. */}
                            <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
                                <legend style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0 6px', color: '#374151' }}>Auto-Remediation</legend>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                                    <input type="checkbox" name="autoRemediationEnabled" checked={form.autoRemediationEnabled} onChange={handleChange} />
                                    Enable auto-remediation for this rule
                                </label>
                                {form.autoRemediationEnabled && (
                                    <>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="sr-wizard">Remediation Wizard</label>
                                                <select id="sr-wizard" name="remediationWizardId" className="form-control" value={form.remediationWizardId} onChange={handleChange}>
                                                    <option value="">— None / Action Item only —</option>
                                                    {wizards.map(w => (
                                                        <option key={w.endpointRemediationWizardId} value={w.endpointRemediationWizardId}>
                                                            {w.name} ({w.category})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 20 }}>
                                                    <input type="checkbox" name="autoExecuteRemediation" checked={form.autoExecuteRemediation} onChange={handleChange} />
                                                    Auto-execute without confirmation
                                                </label>
                                                <small>When unchecked, the wizard is attached to the action item for manual execution by IT staff. Auto-execution is simulated outside Production for safety.</small>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </fieldset>

                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
                                <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                <strong>Enable this rule</strong>
                                <span style={{ color: '#6b7280', fontSize: '0.82rem' }}>(rules are disabled by default)</span>
                            </label>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? <><FaSpinner className="spin" /> Saving&hellip;</> : <><FaSave /> {editingId ? 'Update' : 'Create'} Rule</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Root Component ────────────────────────────────────────────────────────────

const TABS = [
    { id: 'rules',   label: 'Health Rules',          icon: FaShieldAlt },
    { id: 'system',  label: 'System Monitoring',     icon: FaServer },
    { id: 'wizards', label: 'Remediation Wizards',   icon: FaMagic },
    { id: 'scripts', label: 'Script Library',         icon: FaCode },
    { id: 'alerts',  label: 'Alert History',          icon: FaHistory }
];

const EndpointHealthConfigPage = () => {
    const { apiFetch } = useAuth();
    const [activeTab, setActiveTab] = useState('rules');

    return (
        <div>
            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 0 }}>
                {TABS.map(t => {
                    const Icon = t.icon;
                    const active = activeTab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 16px',
                                background: 'none',
                                border: 'none',
                                borderBottom: active ? '2px solid #f63040' : '2px solid transparent',
                                marginBottom: -2,
                                fontWeight: active ? 700 : 400,
                                color: active ? '#f63040' : '#6b7280',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                transition: 'color 0.15s'
                            }}
                        >
                            <Icon size={14} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Tab content */}
            {activeTab === 'rules'   && <HealthRulesTab apiFetch={apiFetch} />}
            {activeTab === 'system'  && <SystemMonitoringTab apiFetch={apiFetch} />}
            {activeTab === 'wizards' && <RemediationWizardsTab apiFetch={apiFetch} />}
            {activeTab === 'scripts' && <ScriptLibraryTab apiFetch={apiFetch} />}
            {activeTab === 'alerts'  && <AlertHistoryTab apiFetch={apiFetch} />}
        </div>
    );
};

export default EndpointHealthConfigPage;
